/**
 * React Hook for Supabase ReportGroups (Supabase Only - No Fallback)
 * Provides pure Supabase-based hooks without SQL Server fallback
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { 
  reportGroupService, 
  ReportGroupFilters, 
  PaginationOptions, 
  ReportGroupResponse 
} from '@/integrations/supabase/reportGroupService';

/**
 * Hook to fetch report groups from Supabase only (no fallback)
 * Returns data in format compatible with Index.tsx
 */
export function useSupabaseReportGroupsOnly(
  filters: ReportGroupFilters = {},
  pagination: PaginationOptions = { limit: 100 },
  enabled: boolean = true
): UseQueryResult<ReportGroupResponse, Error> {
  return useQuery({
    queryKey: ['supabase-report-groups-only', filters, pagination],
    queryFn: () => reportGroupService.getReportGroups(filters, pagination),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2, // Retry twice for network issues
  });
}

/**
 * Compatibility hook that transforms Supabase data to match Index.tsx expectations
 * Maps Supabase report groups format to the format used in Index.tsx
 */
export function useReportGroupsCompatible(filters?: ReportGroupFilters) {
  const { 
    data: groupsResponse, 
    isLoading: loading, 
    error, 
    refetch 
  } = useSupabaseReportGroupsOnly(filters);

  // Transform Supabase data to match Index.tsx expected format
  const reportGroups = groupsResponse?.data?.map(group => ({
    // Primary fields that Index.tsx uses
    ID: group.legacy_id || group.id,
    GroupID: group.legacy_id || group.id,
    id: group.legacy_id || group.id,
    GroupName: group.group_name,
    GroupNameShort: group.group_name, // Use group_name for both
    Descrt: group.description,
    Icon: group.icon,
    UnitID: group.unit_id,
    CompID: group.comp_id,
    UserIDs: group.user_ids,
    STT: group.stt || 0,
    
    // Additional Supabase fields for future use
    supabaseId: group.id,
    syncSource: group.sync_source,
    syncedAt: group.synced_at,
    createdAt: group.created_at,
    updatedAt: group.updated_at,
  })) || [];

  return {
    reportGroups,
    loading,
    error: error?.message || null,
    refresh: refetch,
    // Additional useful data
    count: groupsResponse?.count || 0,
    page: groupsResponse?.page || 1,
    totalPages: groupsResponse?.totalPages || 1,
    hasNextPage: groupsResponse?.hasNextPage || false,
    hasPrevPage: groupsResponse?.hasPrevPage || false,
  };
}

/**
 * Hook to get report group statistics from Supabase only
 */
export function useReportGroupStatsOnly(enabled: boolean = true) {
  return useQuery({
    queryKey: ['report-group-stats-only'],
    queryFn: () => reportGroupService.getReportGroupStats(),
    enabled,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}